import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Calculator, Scale, BadgeCheck, ShieldCheck, Copy, CheckCircle2, Circle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { RateJob } from "@/components/Rating";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/AppLayout";
import { api, formatErr } from "@/lib/api";
import { CheckoutDisclosure } from "@/components/FundingDisclosure";
import { toast } from "sonner";
import { REVIEW_PRICE } from "@/lib/constants";

const SERVICES = [
  { key: "cpa_consult", icon: Calculator, name: "Local CPA budget sign-off", price: 149, desc: "A licensed CPA reviews your grant budget, cost allocations and match before you submit." },
  { key: "attorney_consult", icon: Scale, name: "Business attorney review", price: 249, desc: "A business attorney reviews complex state licensing questions or your entity structure." },
];
const STATUS = { submitted: "Submitted", scheduled: "Scheduled", completed: "Completed" };

function RequestDialog({ svc, onClose }) {
  const [f, setF] = useState({ topic: "", preferred_contact: "" });
  const [busy, setBusy] = useState(false);
  const pay = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/consultations/checkout", { service: svc.key, ...f, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e2) { toast.error(formatErr(e2)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="consult-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{svc.name}</DialogTitle></DialogHeader>
        <form onSubmit={pay} className="space-y-3">
          <div><Label>What do you need help with?</Label><Textarea required minLength={5} rows={4} className="mt-1.5" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} data-testid="consult-topic-input" /></div>
          <div><Label>Best way / time to reach you</Label><Input className="mt-1.5" value={f.preferred_contact} onChange={(e) => setF({ ...f, preferred_contact: e.target.value })} data-testid="consult-contact-input" /></div>
          <Button type="submit" disabled={busy} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="consult-pay-button">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Pay ${svc.price} flat fee</Button>
          <CheckoutDisclosure id="consult" />
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Badge() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/badge").then((r) => setB(r.data)); }, []);
  if (!b) return null;
  const issue = async () => { try { const { data } = await api.post("/badge/issue"); setB({ ...b, badge: data }); toast.success("Your Peer Verified badge is live"); } catch (e) { toast.error(formatErr(e)); } };
  const toggleList = async (v) => {
    try { await api.post("/badge/listing", { listed: v }); setB({ ...b, badge: { ...b.badge, listed: v } }); toast.success(v ? "Listed on the Badge Wall" : "Removed from the Badge Wall"); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const downloadPdf = async () => {
    const res = await api.get("/badge/pdf", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = `Peer_Verified_${b.badge.badge_id}.pdf`;
    a.click();
  };
  const url = b.badge ? `${window.location.origin}/verify/${b.badge.badge_id}` : "";
  const Req = ({ ok, label, to }) => <Link to={to} className="flex items-center gap-2 text-sm">{ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-slate-500" />}{label}</Link>;
  return (
    <div className="bg-slate-900 text-white rounded-lg p-8 border-l-4 border-amber-500" data-testid="badge-section">
      <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-amber-400"><ShieldCheck className="h-4 w-4" />Peer Verified compliance badge</div>
      {b.badge ? (
        <div className="mt-4">
          <h3 className="font-display text-3xl font-extrabold">{b.badge.company} is Peer Verified</h3>
          <p className="text-slate-300 text-sm mt-1">Certificate {b.badge.badge_id} · valid until {b.badge.expires_at}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={url} target="_blank" rel="noreferrer"><Button className="bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="badge-view-certificate-button">View public certificate</Button></a>
            <Button variant="outline" className="bg-transparent text-white border-slate-600" onClick={() => { navigator.clipboard.writeText(url); toast.success("Link copied"); }} data-testid="badge-copy-link-button"><Copy className="h-4 w-4 mr-2" />Copy share link</Button>
            <label className="flex items-center gap-2 text-sm text-slate-200 w-full mt-2"><Switch checked={!!b.badge.listed} onCheckedChange={toggleList} data-testid="badge-wall-listing-switch" />List my business on the public <a href="/verified" target="_blank" rel="noreferrer" className="underline text-amber-300">Badge Wall</a> for lenders & agencies</label>
            <Button variant="outline" className="bg-transparent text-white border-slate-600" onClick={downloadPdf} data-testid="badge-download-pdf-button"><Download className="h-4 w-4 mr-2" />Download certificate PDF</Button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <h3 className="font-display text-2xl font-extrabold">Prove you're fully legitimate</h3>
          <p className="text-slate-300 text-sm mt-1">Show a verifiable certificate to landlords, banks and agencies once you've finished both steps:</p>
          <div className="mt-4 space-y-2"><Req ok={b.licensing_complete} label="Complete every step in a licensing guide" to="/dashboard/licensing" /><Req ok={b.proposal_certified} label="Get a proposal certified by a human editor" to="/dashboard/reviews" /></div>
          <Button onClick={issue} disabled={!b.eligible} className="mt-5 bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="badge-issue-button">Issue my badge</Button>
        </div>
      )}
    </div>
  );
}

export default function Pros() {
  const [svc, setSvc] = useState(null);
  const [mine, setMine] = useState([]);
  useEffect(() => { api.get("/consultations").then((r) => setMine(r.data)); }, []);
  return (
    <div>
      <PageHeader eyebrow="Peer Verify network" title="Certified professionals">Flat-fee, on-demand help from vetted local professionals — on top of our certified grant editors.</PageHeader>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SERVICES.map((s) => (
          <div key={s.key} className="card-lift bg-white border border-slate-200 rounded-lg p-6 flex flex-col" data-testid={`pro-card-${s.key}`}>
            <s.icon className="h-6 w-6 text-amber-600" />
            <h3 className="font-display text-xl font-bold text-slate-900 mt-4">{s.name}</h3>
            <p className="text-sm text-slate-600 mt-2 flex-1">{s.desc}</p>
            <div className="font-display text-4xl font-extrabold text-slate-900 mt-5">${s.price}<span className="text-sm font-normal text-slate-500"> flat</span></div>
            <Button onClick={() => setSvc(s)} className="mt-4 bg-slate-900 hover:bg-slate-800" data-testid={`pro-book-${s.key}`}>Book now</Button>
          </div>
        ))}
        <Link to="/dashboard/proposals" className="card-lift bg-white border border-slate-200 rounded-lg p-6 flex flex-col" data-testid="pro-card-editor">
          <BadgeCheck className="h-6 w-6 text-amber-600" />
          <h3 className="font-display text-xl font-bold text-slate-900 mt-4">Certified grant editor</h3>
          <p className="text-sm text-slate-600 mt-2 flex-1">Final compliance review of a generated proposal. Start it from any proposal page.</p>
          <div className="font-display text-4xl font-extrabold text-slate-900 mt-5">${REVIEW_PRICE}<span className="text-sm font-normal text-slate-500"> flat</span></div>
        </Link>
      </div>
      {mine.length > 0 && (
        <div className="mt-8 bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="my-consultations">
          {mine.map((c) => (
            <div key={c.consult_id} className="p-4">
              <div className="flex justify-between gap-2"><span className="font-semibold text-sm text-slate-900">{c.service_name}</span><span className="text-xs text-amber-700">{STATUS[c.status] || c.status}</span></div>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.topic}</p>
              {c.pro_notes && <p className="text-sm text-slate-700 mt-2 border-l-2 border-amber-400 pl-2">{c.pro_notes}</p>}
              {c.status === "completed" && <RateJob path={`/consultations/${c.consult_id}/rating`} initial={c.my_rating} who={c.assigned_name} testid={`consult-rating-${c.consult_id}`} />}
            </div>
          ))}
        </div>
      )}
      <div className="mt-10"><Badge /></div>
      {svc && <RequestDialog svc={svc} onClose={() => setSvc(null)} />}
    </div>
  );
}
